import React, { useEffect, useState, useCallback } from 'react';
import { useAppStore } from '../store/useAppStore';
import { apiClient } from '../api/client';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { TransactionEditModal } from '../components/TransactionEditModal';
import { TransactionCard } from '../components/TransactionCard';
import { formatCurrency, formatDate, formatDateWithTime, getPeriodDates } from '../utils/format';
import { useTranslation } from 'react-i18next';
import { hapticFeedback } from '../utils/telegram';
import { useWebPowerSyncTransactions } from '../hooks/usePowerSync';
import './TransactionsScreen.css';

type FilterType = 'all' | 'income' | 'expense';
type PeriodType = 'today' | 'week' | 'month' | 'year';

interface TransactionsScreenProps {
  onExportClick?: () => void;
}

export const TransactionsScreen: React.FC<TransactionsScreenProps> = ({ onExportClick }) => {
  const { transactions: storeTransactions, setTransactions, isLoading, setLoading, settings } = useAppStore();
  const { t } = useTranslation();
  
  // PowerSync данные
  const { transactions: psTransactions } = useWebPowerSyncTransactions();
  const transactions = psTransactions.length > 0 ? psTransactions : storeTransactions;
  const [filter, setFilter] = useState<FilterType>('all');
  const [period, setPeriod] = useState<PeriodType>('month');
  const [showInternalTransfers, setShowInternalTransfers] = useState(false);
  const [internalTransfers, setInternalTransfers] = useState<any[]>([]);
  const [summary, setSummary] = useState({
    total_income: 0,
    total_expense: 0,
  });
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Дополнительные параметры выбора периода
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [selectedWeekOffset, setSelectedWeekOffset] = useState(0); // 0 = текущая неделя, -1 = прошлая и т.д.
  const [selectedDayOffset, setSelectedDayOffset] = useState(0); // 0 = сегодня, -1 = вчера и т.д.
  const [customDate, setCustomDate] = useState<string>(''); // для выбора произвольной даты

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      
      // Функция для форматирования даты в локальный формат YYYY-MM-DD
      const formatLocalDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      // Формируем параметры запроса в зависимости от выбранного периода
      let startDate: string | undefined, endDate: string | undefined;
      
      if (period === 'year') {
        const start = new Date(selectedYear, 0, 1);
        const end = new Date(selectedYear, 11, 31);
        startDate = formatLocalDate(start);
        endDate = formatLocalDate(end);
      } else if (period === 'month') {
        const start = new Date(selectedYear, selectedMonth - 1, 1);
        const end = new Date(selectedYear, selectedMonth, 0);
        startDate = formatLocalDate(start);
        endDate = formatLocalDate(end);
      } else if (period === 'week') {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - diff + (selectedWeekOffset * 7));
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        startDate = formatLocalDate(startOfWeek);
        endDate = formatLocalDate(endOfWeek);
      } else if (period === 'today') {
        let targetDate: Date;
        if (customDate) {
          targetDate = new Date(customDate);
        } else {
          targetDate = new Date();
          targetDate.setDate(targetDate.getDate() + selectedDayOffset);
        }
        startDate = formatLocalDate(targetDate);
        endDate = formatLocalDate(targetDate);
      }
      
      // Если есть PowerSync данные — фильтруем локально
      if (psTransactions.length > 0 && startDate && endDate) {
        console.log('💳 [PowerSync] Filtering transactions locally:', startDate, '→', endDate);
        const filtered = psTransactions.filter((t: any) => {
          const d = (t.date || '').slice(0, 10);
          return d >= startDate! && d <= endDate!;
        });
        setTransactions(filtered as any);
        const income = filtered.filter((t: any) => t.type === 'income').reduce((sum: number, t: any) => sum + Number(t.amount), 0);
        const expense = filtered.filter((t: any) => t.type === 'expense').reduce((sum: number, t: any) => sum + Number(t.amount), 0);
        setSummary({ total_income: income, total_expense: expense });
      } else {
        // Fallback на API
        const data = startDate && endDate 
          ? await apiClient.getTransactionsCustom(startDate, endDate)
          : await apiClient.getTransactions(period);
        setTransactions(data.transactions || []);
        const income = data.transactions.filter((t: any) => t.type === 'income').reduce((sum: number, t: any) => sum + t.amount, 0);
        const expense = data.transactions.filter((t: any) => t.type === 'expense').reduce((sum: number, t: any) => sum + t.amount, 0);
        setSummary({ total_income: income, total_expense: expense });
      }
    } catch (error) {
      console.error('❌ Error loading transactions:', error);
      setTransactions([]);
      setSummary({ total_income: 0, total_expense: 0 });
    } finally {
      setLoading(false);
    }
  }, [period, selectedYear, selectedMonth, selectedWeekOffset, selectedDayOffset, customDate, setTransactions, setLoading, settings, psTransactions]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const loadInternalTransfers = useCallback(async () => {
    try {
      setLoading(true);
      
      const formatLocalDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      let startDate, endDate;
      
      if (period === 'year') {
        const start = new Date(selectedYear, 0, 1);
        const end = new Date(selectedYear, 11, 31);
        startDate = formatLocalDate(start);
        endDate = formatLocalDate(end);
      } else if (period === 'month') {
        const start = new Date(selectedYear, selectedMonth - 1, 1);
        const end = new Date(selectedYear, selectedMonth, 0);
        startDate = formatLocalDate(start);
        endDate = formatLocalDate(end);
      } else if (period === 'week') {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - diff + (selectedWeekOffset * 7));
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        startDate = formatLocalDate(startOfWeek);
        endDate = formatLocalDate(endOfWeek);
      } else if (period === 'today') {
        let targetDate: Date;
        if (customDate) {
          targetDate = new Date(customDate);
        } else {
          targetDate = new Date();
          targetDate.setDate(targetDate.getDate() + selectedDayOffset);
        }
        startDate = formatLocalDate(targetDate);
        endDate = formatLocalDate(targetDate);
      }
      
      if (startDate && endDate) {
        const data = await apiClient.getInternalTransfers(startDate, endDate);
        setInternalTransfers(data.transactions || []);
      }
    } catch (error) {
      console.error('❌ Error loading internal transfers:', error);
      setInternalTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [period, selectedYear, selectedMonth, selectedWeekOffset, selectedDayOffset, customDate, setLoading]);

  useEffect(() => {
    if (showInternalTransfers) {
      loadInternalTransfers();
    }
  }, [showInternalTransfers, loadInternalTransfers]);

  const getFilteredTransactions = () => {
    // Если показываем внутренние переводы, используем их вместо обычных транзакций
    const sourceTransactions = showInternalTransfers ? internalTransfers : transactions;
    let filtered = sourceTransactions;
    
    // Фильтр по типу (доход/расход) - для внутренних переводов не применяем
    if (!showInternalTransfers && filter !== 'all') {
      filtered = filtered.filter(t => t.type === filter);
    }
    
    // Поиск по названию, категории и дате
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(t => {
        // Поиск по названию
        const matchTitle = t.title?.toLowerCase().includes(query);
        
        // Поиск по категории
        const matchCategory = t.category?.toLowerCase().includes(query);
        
        // Поиск по дате (формат: "5 Oct, 3:45 PM")
        const formattedDate = formatDateWithTime(t.date).toLowerCase();
        const matchDate = formattedDate.includes(query);
        
        // Поиск по дате (формат: "5 Oct")
        const shortDate = formatDate(t.date, 'd MMM').toLowerCase();
        const matchShortDate = shortDate.includes(query);
        
        return matchTitle || matchCategory || matchDate || matchShortDate;
      });
    }
    
    return filtered;
  };

  const handleUpdateTransaction = async (transaction: any) => {
    try {
      setIsSubmitting(true);
      
      // Для Plaid транзакций используем специальный API
      if (transaction.source === 'plaid' && transaction.plaid_transaction_id) {
        console.log('💾 Updating Plaid transaction category:', {
          plaidTransactionId: transaction.plaid_transaction_id,
          category: transaction.category,
        });

        const response = await apiClient.updatePlaidTransactionCategory(
          transaction.plaid_transaction_id,
          transaction.category
        );

        if (response.success) {
          hapticFeedback.success();
          setShowTransactionDetails(false);
          setSelectedTransaction(null);
          await loadTransactions();
        }
        return;
      }
      
      // Для обычных транзакций используем стандартный API
      const transactionId = parseInt(transaction.id.toString().replace(/\D/g, ''));
      
      console.log('💾 Updating transaction:', {
        id: transactionId,
        type: transaction.type,
        amount: transaction.amount,
        title: transaction.title,
        category: transaction.category,
        date: transaction.date
      });

      const response = await apiClient.updateTransaction(transactionId, {
        type: transaction.type,
        amount: transaction.amount,
        title: transaction.title,
        category: transaction.category,
        date: transaction.date
      });

      if (response.success) {
        hapticFeedback.success();
        setShowTransactionDetails(false);
        setSelectedTransaction(null);
        await loadTransactions();
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      hapticFeedback.error();
      alert(t('transaction.updateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      // Не удаляем транзакции из Plaid
      if (selectedTransaction?.source === 'plaid') {
        console.log('⚠️ Cannot delete Plaid transactions');
        alert('Транзакции из банковских счетов нельзя удалить. Они синхронизируются автоматически.');
        return;
      }
      
      setIsSubmitting(true);
      
      const transactionId = parseInt(selectedTransaction.id.toString().replace(/\D/g, ''));
      
      console.log('🗑️ Deleting transaction:', {
        id: transactionId,
        type: selectedTransaction.type
      });

      const response = await apiClient.deleteTransaction(transactionId, selectedTransaction.type);

      if (response.success) {
        hapticFeedback.success();
        setShowTransactionDetails(false);
        setSelectedTransaction(null);
        await loadTransactions();
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      hapticFeedback.error();
      alert(t('transaction.deleteError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filters: { id: FilterType; label: string; icon: string }[] = [
    { id: 'all', icon: 'list', label: t('transactions.all') },
    { id: 'income', icon: 'trending-up', label: t('home.income') },
    { id: 'expense', icon: 'trending-down', label: t('home.expenses') },
  ];

  const periods = [
    { id: 'today' as PeriodType, label: t('transactions.today') },
    { id: 'week' as PeriodType, label: t('analytics.periods.week') || t('transactions.week') },
    { id: 'month' as PeriodType, label: t('analytics.periods.month') || t('transactions.month') },
    { id: 'year' as PeriodType, label: t('analytics.periods.year') || t('transactions.year') },
  ];

  return (
    <div className="transactions-screen page">
      {/* Period Selector */}
      <div className="section">
        <div className="period-selector">
          {periods.map((p) => (
            <button
              key={p.id}
              className={`period-selector__item ${period === p.id ? 'period-selector__item--active neo-raised neo-static' : ''}`}
              onClick={() => {
                setPeriod(p.id);
                // Сброс на текущий период при смене типа
                if (p.id === 'today') {
                  setSelectedDayOffset(0);
                  setCustomDate('');
                }
                if (p.id === 'week') setSelectedWeekOffset(0);
                if (p.id === 'month' || p.id === 'year') {
                  setSelectedYear(new Date().getFullYear());
                  setSelectedMonth(new Date().getMonth() + 1);
                }
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Day Selector */}
      {period === 'today' && (
        <div className="section">
          <div className="day-selector">
            <button 
              className="day-nav-btn neo-raised"
              onClick={() => {
                setCustomDate('');
                setSelectedDayOffset(selectedDayOffset - 1);
              }}
            >
              <Icon name="chevron-left" size={20} />
            </button>
            <div className="day-content">
              <span className="day-label">
                {customDate ? (
                  new Date(customDate).toLocaleDateString('ru-RU', { 
                    day: 'numeric', 
                    month: 'long',
                    year: 'numeric'
                  })
                ) : selectedDayOffset === 0 ? (
                  'Сегодня'
                ) : selectedDayOffset === -1 ? (
                  'Вчера'
                ) : selectedDayOffset === -2 ? (
                  'Позавчера'
                ) : selectedDayOffset < 0 ? (
                  `${Math.abs(selectedDayOffset)} дней назад`
                ) : selectedDayOffset === 1 ? (
                  'Завтра'
                ) : (
                  `Через ${selectedDayOffset} дней`
                )}
              </span>
              <input
                type="date"
                className="day-date-picker"
                value={customDate || (() => {
                  const date = new Date();
                  date.setDate(date.getDate() + selectedDayOffset);
                  return date.toISOString().split('T')[0];
                })()}
                onChange={(e) => {
                  setCustomDate(e.target.value);
                  setSelectedDayOffset(0);
                }}
                style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  border: '1px solid #E5E5EA',
                  borderRadius: '8px',
                  fontSize: '14px',
                  width: '100%',
                  cursor: 'pointer'
                }}
              />
            </div>
            <button 
              className="day-nav-btn neo-raised"
              onClick={() => {
                setCustomDate('');
                setSelectedDayOffset(selectedDayOffset + 1);
              }}
            >
              <Icon name="chevron-right" size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Week Selector */}
      {period === 'week' && (
        <div className="section">
          <div className="week-selector">
            <button 
              className="week-nav-btn neo-raised"
              onClick={() => setSelectedWeekOffset(selectedWeekOffset - 1)}
            >
              <Icon name="chevron-left" size={20} />
            </button>
            <span className="week-label">
              {selectedWeekOffset === 0 && 'Текущая неделя'}
              {selectedWeekOffset === -1 && 'Прошлая неделя'}
              {selectedWeekOffset < -1 && `${Math.abs(selectedWeekOffset)} недели назад`}
              {selectedWeekOffset === 1 && 'Следующая неделя'}
              {selectedWeekOffset > 1 && `Через ${selectedWeekOffset} недели`}
            </span>
            <button 
              className="week-nav-btn neo-raised"
              onClick={() => setSelectedWeekOffset(selectedWeekOffset + 1)}
            >
              <Icon name="chevron-right" size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Month Selector */}
      {period === 'month' && (
        <div className="section">
          <div className="month-selector">
            <button 
              className="month-nav-btn neo-raised"
              onClick={() => {
                if (selectedMonth === 1) {
                  setSelectedMonth(12);
                  setSelectedYear(selectedYear - 1);
                } else {
                  setSelectedMonth(selectedMonth - 1);
                }
              }}
            >
              <Icon name="chevron-left" size={20} />
            </button>
            <div className="month-year-picker">
              <select 
                className="month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
                  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'].map((month, i) => (
                  <option key={i + 1} value={i + 1}>{month}</option>
                ))}
              </select>
              <select 
                className="year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <button 
              className="month-nav-btn neo-raised"
              onClick={() => {
                if (selectedMonth === 12) {
                  setSelectedMonth(1);
                  setSelectedYear(selectedYear + 1);
                } else {
                  setSelectedMonth(selectedMonth + 1);
                }
              }}
            >
              <Icon name="chevron-right" size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Year Selector */}
      {period === 'year' && (
        <div className="section">
          <div className="year-selector">
            <button 
              className="year-nav-btn neo-raised"
              onClick={() => setSelectedYear(selectedYear - 1)}
            >
              <Icon name="chevron-left" size={20} />
            </button>
            <select 
              className="year-select-large"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>{year} год</option>
              ))}
            </select>
            <button 
              className="year-nav-btn neo-raised"
              onClick={() => setSelectedYear(selectedYear + 1)}
            >
              <Icon name="chevron-right" size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="section">
        <div className="summary-grid">
          <Card padding="medium" className="summary-item summary-item--income neo-static">
            <div className="summary-item__icon">
              <Icon name="trending-up" size={20} />
            </div>
            <div className="summary-item__content">
              <div className="summary-item__label">{t('home.income')}</div>
              <div className="summary-item__value">
                {formatCurrency(summary.total_income, settings?.currency || 'RUB')}
              </div>
            </div>
          </Card>

          <Card padding="medium" className="summary-item summary-item--expense neo-static">
            <div className="summary-item__icon">
              <Icon name="trending-down" size={20} />
            </div>
            <div className="summary-item__content">
              <div className="summary-item__label">{t('home.expenses')}</div>
              <div className="summary-item__value">
                {formatCurrency(summary.total_expense, settings?.currency || 'RUB')}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Filters */}
      <div className="section">
        <div className="filter-chips">
          {!showInternalTransfers && filters.map((f) => (
            <button
              key={f.id}
              className={`filter-chip ${filter === f.id ? 'filter-chip--active neo-raised' : ''} ${filter === f.id && f.id === 'income' ? 'filter-chip--income' : ''} ${filter === f.id && f.id === 'expense' ? 'filter-chip--expense' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              <span className="filter-chip__icon">
                <Icon name={f.icon} size={16} />
              </span>
              <span className="filter-chip__label">{f.label}</span>
            </button>
          ))}
          <button
            className={`filter-chip ${showInternalTransfers ? 'filter-chip--active neo-raised' : ''}`}
            onClick={() => {
              setShowInternalTransfers(!showInternalTransfers);
              setFilter('all'); // Сбрасываем фильтр при переключении
            }}
          >
            <span className="filter-chip__icon">
              <Icon name="swap-horizontal" size={16} />
            </span>
            <span className="filter-chip__label">Внутренние переводы</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="section">
        <div className="search-container">
          <Icon name="search" size={20} color="#8E8E93" />
          <input
            type="text"
            className="search-input"
            placeholder={t('transactions.searchPlaceholder') || 'Поиск по названию, категории или дате...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="search-clear"
              onClick={() => setSearchQuery('')}
            >
              <Icon name="close" size={18} color="#8E8E93" />
            </button>
          )}
        </div>
      </div>

      {/* Transactions List */}
      <div className="section">
        {getFilteredTransactions().length > 0 ? (
          <div className="transactions-list">
              {getFilteredTransactions().map((transaction, index) => (
                <TransactionCard
                  key={transaction.id}
                  transaction={transaction}
                  currency={settings?.currency || 'RUB'}
                  isLast={index === getFilteredTransactions().length - 1}
                  onClick={(t) => {
                    setSelectedTransaction(t);
                    setShowTransactionDetails(true);
                  }}
                />
              ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state__icon">
              <Icon name="list" size={48} color="#8E8E93" />
            </div>
            <div className="empty-state__title">
              {showInternalTransfers 
                ? 'Нет внутренних переводов'
                : filter === 'all' ? t('home.noTransactions') : 
                  filter === 'income' ? t('transactions.noIncome') : t('transactions.noExpenses')}
            </div>
            <div className="empty-state__subtitle">
              {showInternalTransfers
                ? 'Внутренние переводы между вашими счетами будут отображаться здесь'
                : filter === 'all' 
                  ? t('home.addFirstTransaction')
                  : t('transactions.noTransactionsForPeriod', { type: filter === 'income' ? t('transactions.noIncome').toLowerCase() : t('transactions.noExpenses').toLowerCase() })
              }
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details Modal */}
      {showTransactionDetails && selectedTransaction && (
        <TransactionEditModal
          transaction={selectedTransaction}
          isSubmitting={isSubmitting}
          onUpdate={handleUpdateTransaction}
          onDelete={handleDeleteTransaction}
          onClose={() => {
            setShowTransactionDetails(false);
            const wasPlaidTransaction = selectedTransaction?.source === 'plaid';
            setSelectedTransaction(null);
            if (wasPlaidTransaction) {
              loadTransactions();
              if (showInternalTransfers) {
                loadInternalTransfers();
              }
            }
          }}
        />
      )}
    </div>
  );
};
